import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { ChevronDown, Copy, Check, Plus, Search } from "lucide-react";
import { money, FREQ_LABEL } from "@/lib/brand";

const inputCls = "bg-white border-[#E2EBF0] focus:border-[#10B981] focus:ring-2 focus:ring-[#67F2B3]/40";

export default function NewServiceDialog({ onCreated }) {
  const { serviceOpen, setServiceOpen, servicePrefill, refresh, openNewClient } = useAdmin();
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [form, setForm] = useState(blank());
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  function blank() {
    return {
      client_id: "", name: "", description: "", payment_type: "one_time",
      frequency: "monthly", amount: "", notes: "", terms: "", start_date: "",
      contract_end_date: "", contract_ref: "", instructions: "", presentation_type: "general", annual_option: false,
    };
  }

  useEffect(() => {
    if (serviceOpen) {
      setResult(null);
      setForm({ ...blank(), client_id: servicePrefill || "" });
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [serviceOpen, servicePrefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const filtered = clients.filter((c) =>
    [c.name, c.email, c.company].join(" ").toLowerCase().includes(clientSearch.toLowerCase())
  );
  const selectedClient = clients.find((c) => c.client_id === form.client_id);

  const validate = () => {
    if (!form.client_id) return "Select a client";
    if (!form.name) return "Service name is required";
    if (!form.amount || Number(form.amount) <= 0) return "Enter a valid price";
    return null;
  };

  const buildPayload = () => ({
    client_id: form.client_id,
    name: form.name,
    description: form.description,
    payment_type: form.payment_type,
    frequency: form.payment_type === "subscription" ? form.frequency : null,
    amount: Number(form.amount),
    notes: form.notes, terms: form.terms, start_date: form.start_date,
    contract_end_date: form.contract_end_date, presentation_type: form.presentation_type,
    contract_ref: form.contract_ref, instructions: form.instructions,
    annual_option: form.payment_type === "subscription" && form.frequency === "monthly" && form.annual_option,
  });

  const submit = async (action) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const svc = await api.post("/services", buildPayload());
      const serviceId = svc.data.service_id;
      if (action === "draft") {
        toast.success("Service draft saved");
        finish();
        return;
      }
      const origin = window.location.origin;
      if (form.annual_option && form.payment_type === "subscription" && form.frequency === "monthly") {
        const r = await api.post(`/services/${serviceId}/send-invitation`, { origin_url: origin, send_email: action === "send" });
        setResult({ url: r.data.activation_link, sent: r.data.email_sent, configured: action === "send" ? r.data.email_configured : true, choice: true });
      } else if (action === "link") {
        const r = await api.post("/payments/checkout", { service_id: serviceId, origin_url: origin });
        setResult({ url: r.data.checkout_url, sent: false, configured: true });
      } else {
        const r = await api.post("/payments/create-and-send", { service_id: serviceId, origin_url: origin });
        setResult({ url: r.data.checkout_url, sent: r.data.email_sent, configured: r.data.email_configured });
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create service");
    } finally {
      setSaving(false);
    }
  };

  const finish = () => {
    setServiceOpen(false);
    refresh();
    if (onCreated) onCreated();
  };

  const copy = () => {
    navigator.clipboard.writeText(result.url);
    setCopied(true);
    toast.success(result.choice ? "Plan-selection link copied" : "Checkout link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={serviceOpen} onOpenChange={setServiceOpen}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0D3B2D]">New Service</DialogTitle>
          <DialogDescription className="text-[#4A635A]">Create any custom service and collect a one-time or recurring payment.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 pt-2" data-testid="service-result">
            <div className="bg-[#F4FFF9] border border-[#BCECD8] rounded-xl p-4">
              <div className="font-mono text-xs uppercase tracking-widest text-[#10B981] mb-1">{result.choice ? "Plan-selection link ready (Monthly or Yearly)" : "Checkout link ready"}</div>
              <p className="text-sm text-[#0D3B2D] break-all font-mono">{result.url}</p>
            </div>
            {result.configured ? (
              result.sent ? (
                <p className="text-sm text-[#10B981]" data-testid="email-sent-note">✓ Invitation email sent to the client.</p>
              ) : null
            ) : (
              <p className="text-sm text-[#4A635A]" data-testid="email-not-configured-note">
                Email isn't configured yet — copy the link and send it to your client.
              </p>
            )}
            <div className="flex gap-2">
              <Button onClick={copy} data-testid="copy-checkout-link-btn"
                className="flex-1 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">
                {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />} Copy Link
              </Button>
              <Button onClick={finish} variant="outline" data-testid="service-done-btn"
                className="flex-1 border-[#E2EBF0] rounded-lg">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {/* Client selector */}
            <div>
              <Label className="text-[#0D3B2D]">Client *</Label>
              {selectedClient ? (
                <div className="flex items-center justify-between bg-[#F4FFF9] border border-[#BCECD8] rounded-lg px-3 py-2 mt-1">
                  <span className="text-sm text-[#0D3B2D]">{selectedClient.name} <span className="text-[#4A635A]">· {selectedClient.email}</span></span>
                  <button onClick={() => set("client_id", "")} className="text-xs text-[#10B981]" data-testid="change-client-btn">Change</button>
                </div>
              ) : (
                <div className="mt-1 border border-[#E2EBF0] rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E2EBF0]">
                    <Search className="h-4 w-4 text-[#4A635A]" />
                    <input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Search clients…" data-testid="service-client-search"
                      className="flex-1 text-sm outline-none bg-transparent" />
                    <button onClick={openNewClient} className="text-xs text-[#10B981] flex items-center gap-1" data-testid="service-add-client-btn">
                      <Plus className="h-3 w-3" /> New
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {filtered.length === 0 && <div className="px-3 py-3 text-sm text-[#4A635A]">No clients found</div>}
                    {filtered.map((c) => (
                      <button key={c.client_id} onClick={() => set("client_id", c.client_id)}
                        data-testid={`service-client-option-${c.client_id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#F4FFF9] text-sm text-[#0D3B2D]">
                        {c.name} <span className="text-[#4A635A]">· {c.email}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-[#0D3B2D]">Service Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls}
                data-testid="service-name-input" placeholder="e.g. Monthly Website Maintenance" />
            </div>

            <div>
              <Label className="text-[#0D3B2D]">Description</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                className={inputCls} data-testid="service-description-input" placeholder="What the client receives (optional)" rows={2} />
            </div>

            <div>
              <Label className="text-[#0D3B2D]">Service Presentation Type</Label>
              <select value={form.presentation_type} onChange={(e) => set("presentation_type", e.target.value)}
                data-testid="service-presentation-select"
                className={`${inputCls} w-full h-10 rounded-md border px-3 text-sm text-[#0D3B2D]`}>
                <option value="general">General Service</option>
                <option value="technology">Technology / Infrastructure</option>
                <option value="website">Website / Web Development</option>
                <option value="code">Code / Software</option>
                <option value="managed">Managed Digital Service</option>
                <option value="content">Content Updates</option>
                <option value="email">Email &amp; Communications</option>
                <option value="administrative">Administrative Support</option>
                <option value="custom">Custom</option>
              </select>
              <p className="text-[11px] text-[#4A635A] mt-1">Technology, Website and Code services show the SYSTEM CORE terminal by default; others show a clean service panel.</p>
            </div>

            {/* Payment type */}
            <div>
              <Label className="text-[#0D3B2D]">Payment Type *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[["one_time", "One-Time Payment"], ["subscription", "Subscription"]].map(([v, l]) => (
                  <button key={v} onClick={() => set("payment_type", v)} data-testid={`service-type-${v}`}
                    className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      form.payment_type === v
                        ? "bg-[#0D3B2D] text-white border-[#0D3B2D]"
                        : "bg-white text-[#0D3B2D] border-[#E2EBF0] hover:border-[#BCECD8]"
                    }`}>{l}</button>
                ))}
              </div>
            </div>

            {form.payment_type === "subscription" && (
              <div data-testid="frequency-section">
                <Label className="text-[#0D3B2D]">Billing Frequency *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {Object.keys(FREQ_LABEL).map((f) => (
                    <button key={f} onClick={() => set("frequency", f)} data-testid={`service-freq-${f}`}
                      className={`py-2 rounded-lg text-xs font-medium border capitalize transition-colors ${
                        form.frequency === f
                          ? "bg-[#67F2B3] text-[#071A13] border-[#67F2B3]"
                          : "bg-white text-[#0D3B2D] border-[#E2EBF0]"
                      }`}>{f}</button>
                  ))}
                </div>
              </div>
            )}

            {form.payment_type === "subscription" && form.frequency === "monthly" && (
              <label className="flex items-center gap-2 text-sm text-[#0D3B2D] cursor-pointer" data-testid="annual-option-label">
                <input type="checkbox" checked={form.annual_option} onChange={(e) => set("annual_option", e.target.checked)}
                  data-testid="annual-option-checkbox" className="h-4 w-4 accent-[#10B981]" />
                Offer Annual Payment — Save 20%
              </label>
            )}

            <div>
              <Label className="text-[#0D3B2D]">{form.annual_option && form.payment_type === "subscription" && form.frequency === "monthly" ? "Monthly Price (USD) *" : "Price (USD) *"}</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A635A] font-mono">$</span>
                <Input value={form.amount} onChange={(e) => set("amount", e.target.value)} type="number" step="0.01" min="0"
                  className={`${inputCls} pl-7 font-mono`} data-testid="service-price-input" placeholder="49.99" />
              </div>
            </div>

            {/* More options */}
            <Collapsible open={more} onOpenChange={setMore}>
              <CollapsibleTrigger className="flex items-center gap-1 text-sm text-[#10B981] font-medium" data-testid="more-options-toggle">
                More Options <ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-3">
                <div>
                  <Label className="text-[#0D3B2D]">Internal Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} rows={2} data-testid="service-notes-input" />
                </div>
                <div>
                  <Label className="text-[#0D3B2D]">Service Terms</Label>
                  <Textarea value={form.terms} onChange={(e) => set("terms", e.target.value)} className={inputCls} rows={2} data-testid="service-terms-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#0D3B2D]">Start Date</Label>
                    <Input value={form.start_date} onChange={(e) => set("start_date", e.target.value)} type="date" className={inputCls} data-testid="service-start-input" />
                  </div>
                  <div>
                    <Label className="text-[#0D3B2D]">Contract End Date</Label>
                    <Input value={form.contract_end_date} onChange={(e) => set("contract_end_date", e.target.value)} type="date" className={inputCls} data-testid="service-contract-end-input" />
                  </div>
                  <div>
                    <Label className="text-[#0D3B2D]">Contract Ref</Label>
                    <Input value={form.contract_ref} onChange={(e) => set("contract_ref", e.target.value)} className={inputCls} data-testid="service-contract-input" />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {form.amount > 0 && form.payment_type === "subscription" && form.frequency === "monthly" && form.annual_option ? (
              <AnnualPreview monthly={Number(form.amount)} />
            ) : form.amount > 0 && (
              <div className="bg-[#F8FAF9] border border-[#E2EBF0] rounded-lg px-4 py-3 text-sm text-[#0D3B2D]">
                <span className="font-mono font-semibold text-base">{money(form.amount)}</span>{" "}
                {form.payment_type === "subscription" ? `· ${FREQ_LABEL[form.frequency]}` : "· one-time"}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2">
              <div className="flex gap-2">
                <Button onClick={() => submit("draft")} disabled={saving} variant="outline"
                  data-testid="save-draft-button" className="flex-1 border-[#E2EBF0] rounded-lg">Save Draft</Button>
                <Button onClick={() => submit("link")} disabled={saving}
                  data-testid="create-link-button" className="flex-1 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">
                  Create Payment Link
                </Button>
              </div>
              <Button onClick={() => submit("send")} disabled={saving}
                data-testid="create-send-button" className="bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">
                Create &amp; Send
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const annual = (m) => {
  const regular = Math.round(m * 12 * 100) / 100;
  const yearly = Math.round(regular * 0.8 * 100) / 100;
  return { regular, yearly, savings: Math.round((regular - yearly) * 100) / 100, eq: Math.round((yearly / 12) * 100) / 100 };
};

function AnnualPreview({ monthly }) {
  const a = annual(monthly);
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="annual-preview">
      <div className="bg-[#F8FAF9] border border-[#E2EBF0] rounded-lg px-3 py-3 text-sm text-[#0D3B2D]" data-testid="annual-preview-monthly">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A635A]">Monthly</div>
        <div className="font-mono font-semibold text-base mt-1">{money(monthly)}/month</div>
        <div className="text-[11px] text-[#4A635A]">Billed monthly</div>
      </div>
      <div className="bg-[#F4FFF9] border border-[#BCECD8] rounded-lg px-3 py-3 text-sm text-[#0D3B2D]" data-testid="annual-preview-yearly">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#10B981]">Yearly — Save 20%</div>
        <div className="font-mono font-semibold text-base mt-1">{money(a.yearly)}/year</div>
        <div className="text-[11px] text-[#4A635A]">≈ {money(a.eq)}/month · save {money(a.savings)}/yr</div>
      </div>
    </div>
  );
}
